import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import NavLink from '../NavLink/navlink-component';
import SystemDataService from '../../Services/SystemDataService';
import MediaComponent from '../Media/media-component';
import { getPromotionData } from '../../Public/promotion-data';

/* ── Icons ───────────────────────────────────────────────────────────────── */
const iconProps = { fill: 'none', viewBox: '0 0 24 24', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' };

const FeatureIcons = {
    calendar: (c) => (<svg className={c} {...iconProps}><rect x="3" y="4.5" width="18" height="16" rx="2" /><path d="M3 9h18M8 2.5v4M16 2.5v4" /></svg>),
    users: (c) => (<svg className={c} {...iconProps}><path d="M17 20v-1.5a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4V20" /><circle cx="9.5" cy="7.5" r="3.5" /><path d="M22 20v-1.5a4 4 0 0 0-3-3.87M16 4.13a4 4 0 0 1 0 7.75" /></svg>),
    star: (c) => (<svg className={c} {...iconProps}><path d="M12 3l2.9 5.9 6.5.9-4.7 4.6 1.1 6.5L12 18.8 6.2 21.9l1.1-6.5L2.6 9.8l6.5-.9L12 3z" /></svg>),
    gift: (c) => (<svg className={c} {...iconProps}><path d="M20 12v8H4v-8M2 7h20v5H2zM12 22V7M12 7S11 3 8.5 3 6 5.5 7 7M12 7s1-4 3.5-4S18 5.5 17 7" /></svg>),
};

function ArrowIcon({ className = 'h-5 w-5' }) {
    return (<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}><path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" /></svg>);
}
function HeartIcon({ className = 'h-5 w-5' }) {
    return (<svg className={className} viewBox="0 0 24 24" fill="currentColor"><path d="M12 21s-6.7-4.35-9.33-7.5C.9 11.28 1.06 8.1 3.2 6.4a4.6 4.6 0 0 1 6.3.5L12 9.4l2.5-2.5a4.6 4.6 0 0 1 6.3-.5c2.14 1.7 2.3 4.88.53 7.1C18.7 16.65 12 21 12 21z" /></svg>);
}
function LockIcon({ className = 'h-3.5 w-3.5' }) {
    return (<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><rect x="4.5" y="10.5" width="15" height="10" rx="2" /><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" /></svg>);
}

/* ── Section ─────────────────────────────────────────────────────────────── */
function PromotionSection({ data }) {
    const { dir = 'rtl', language = 'he', links = [], businessInfo = {} } = useSelector((s) => s.systemData || {});
    const brandName = businessInfo?.businessName || '';

    const membership = useMemo(() => data || getPromotionData(language).membership, [data, language]);
    const logoWhite = SystemDataService.byLabel(links, 'Logo White');

    const {
        heroImage,
        badge,
        heroNumber,
        eyebrow,
        titleTop,
        titleBottom,
        subtitle,
        membersCount,
        membersNote,
        extraCount,
        avatars = [],
        features = [],
        ctaText,
        communityCtaText,
        secureNote,
    } = membership || {};

    // Primary CTA → paid 770 Club (subscriptions); secondary → free community.
    const clubHref = '/subscriptions';
    const communityHref = '/community/join';

    return (
        <section dir={dir} className="relative w-full bg-white overflow-hidden">
            {/* ── Hero image across, taller on desktop so the fade is gradual ── */}
            <div className="relative w-full h-[26vh] min-h-[180px] max-h-[300px] lg:h-[46vh] lg:min-h-[360px] lg:max-h-[520px]">
                {heroImage && (
                    <MediaComponent src={heroImage} alt="" useSekeleton={false} wrapperClassName="w-full h-full" />
                )}
                {/* Fade to white, full white at the bottom so it merges seamlessly with the sheet/section */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/5 to-white" />

                {/* Logo */}
                {logoWhite?.image && (
                    <img
                        src={logoWhite.image}
                        alt={brandName || 'Logo'}
                        className="absolute left-1/2 top-6 md:top-8 -translate-x-1/2 h-10 md:h-12 lg:h-14 w-auto object-contain drop-shadow-lg"
                    />
                )}

                {/* Big 770 over the image */}
                {heroNumber && (
                    <span className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-primary font-bold leading-none text-white drop-shadow-[0_6px_30px_rgba(0,0,0,0.55)] text-[30vw] lg:text-[15rem]">
                        {heroNumber}
                    </span>
                )}
            </div>

            {/* ── Content sheet, overlaps up over the image to hide the seam ── */}
            <div className="relative z-10 mx-auto w-full max-w-[520px] lg:max-w-6xl px-6 lg:px-10">
                <div className="-mt-6 lg:-mt-28 lg:rounded-[2.5rem] lg:bg-white lg:px-12 lg:pt-14 lg:pb-16 lg:shadow-[0_-24px_70px_-30px_rgba(0,0,0,0.18)] pb-14 flex flex-col items-center lg:items-stretch">

                    {/* On desktop: two balanced columns; on mobile: single centered column */}
                    <div className="w-full lg:grid lg:grid-cols-2 lg:gap-x-14 lg:items-start flex flex-col items-center">

                        {/* ── Left: narrative + social proof ── */}
                        <div className="w-full flex flex-col items-center text-center lg:items-start lg:text-start">
                         
                            {eyebrow && (
                                <span className="text-secondary-500 text-md font-normal md:text-md ">
                                    {eyebrow}
                                </span>
                            )}

                            <motion.h2
                                className="font-secondary font-extralight uppercase leading-[0.95] mt-2 text-4xl md:text-5xl lg:text-6xl"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5 }}
                            >
                                <span className="block text-primary-900">{titleTop}</span>
                                <span className="block text-secondary-500">{titleBottom}</span>
                            </motion.h2>

                            {subtitle && (
                                <p className="text-primary-400 text-sm md:text-base leading-relaxed mt-3 max-w-sm lg:max-w-md">
                                    {subtitle}
                                </p>
                            )}

                            {/* ── Members card ── */}
                            <div className="w-full mt-6 rounded-2xl bg-secondary-50/70 ring-1 ring-secondary-100 px-5 py-5 flex flex-col items-center lg:items-start">
                                <div dir="ltr" className="flex items-center -space-x-2.5">
                                    {avatars.filter(Boolean).map((src, i) => (
                                        <span key={i} className="h-9 w-9 md:h-10 md:w-10 rounded-full ring-2 ring-white overflow-hidden bg-secondary-100">
                                            <MediaComponent src={src} alt="" useSekeleton={false} wrapperClassName="w-full h-full" />
                                        </span>
                                    ))}
                                    {extraCount && (
                                        <span className="h-9 w-9 md:h-10 md:w-10 rounded-full ring-2 ring-white bg-secondary-500 text-white font-bold text-xs flex items-center justify-center">
                                            {extraCount}
                                        </span>
                                    )}
                                </div>
                                {membersCount && <p className="mt-3 text-secondary-600 font-bold text-base md:text-lg">{membersCount}</p>}
                                {membersNote && <p className="text-primary-400 text-xs md:text-sm lg:text-start text-center">{membersNote}</p>}
                            </div>
                        </div>

                        {/* ── Right: features + CTA ── */}
                        <div className="w-full mt-5 lg:mt-0 flex flex-col">
                            {/* ── Feature list ── */}
                            <div className="w-full rounded-2xl ring-1 ring-primary-100 divide-y divide-primary-100 overflow-hidden">
                                {features.map((f, i) => {
                                    const Icon = FeatureIcons[f.icon] || FeatureIcons.star;
                                    const Row = f.href ? 'a' : 'div';
                                    return (
                                        <Row
                                            key={i}
                                            href={f.href}
                                            className={`group flex items-center gap-3.5 px-4 py-3.5 lg:py-4 text-start ${f.href ? 'hover:bg-secondary-50/60 transition-colors cursor-pointer' : ''}`}
                                        >
                                            <span className="shrink-0 h-9 w-9 rounded-full bg-secondary-100 text-secondary-600 flex items-center justify-center">
                                                {Icon('h-4 w-4')}
                                            </span>
                                            <span className="flex-1 flex flex-col">
                                                <span className="text-primary-900 font-bold text-sm uppercase">{f.title}</span>
                                                <span className="text-primary-400 text-xs md:text-sm leading-snug mt-0.5">{f.detail}</span>
                                            </span>
                                            <ArrowIcon className="h-4 w-4 shrink-0 text-secondary-400 transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180" />
                                        </Row>
                                    );
                                })}
                            </div>

                            {/* ── CTAs: join the 770 Club (primary) + join the community (secondary) ── */}
                            <NavLink
                                href={clubHref}
                                showUnderline={false}
                                preserveStyle
                                dir="ltr"
                                className="group w-full mt-6 flex items-center justify-center gap-3 rounded-full bg-secondary-500 px-6 py-4 text-white font-bold uppercase text-sm shadow-lg shadow-secondary-500/25 transition-all duration-300 hover:bg-secondary-600 hover:shadow-secondary-500/40 active:scale-[0.98] !opacity-100"
                            >
                                <span>{ctaText}</span>
                            </NavLink>

                            {communityCtaText && (
                                <NavLink
                                    href={communityHref}
                                    showUnderline={false}
                                    preserveStyle
                                    dir="ltr"
                                    className="group w-full mt-3 flex items-center justify-center gap-3 rounded-full border border-primary-200 bg-white px-6 py-4 text-primary-900 font-bold uppercase text-sm transition-all duration-300 hover:border-secondary-400 hover:text-secondary-600 active:scale-[0.98] !opacity-100"
                                >
                                    <span>{communityCtaText}</span>
                                    <ArrowIcon className="h-4 w-4 shrink-0 transition-transform duration-300 group-hover:translate-x-1" />
                                </NavLink>
                            )}

                            {secureNote && (
                                <p className="mt-3 flex items-center justify-center lg:justify-start gap-1.5 text-primary-400 text-xs">
                                    <LockIcon className="h-3.5 w-3.5 text-secondary-500" />
                                    {secureNote}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default PromotionSection;
